// 🔧 public_links.js
// ✅ Purpose: Admin-only page showing public-facing event links and QR codes
// ✅ Features:
// - Replaces qr_links.js
// - Shows only event/public visitor links, not platform account links
// - Generates QR codes for each public event page
// - Copies full URLs to clipboard
// - Conditionally shows auction links when auction is enabled
// - Uses standard admin page layout

import React, { useMemo, useState } from "react";
import QRCode from "react-qr-code";
import { useEventContext } from "../../hooks/use_event_context";
import FullPageSpinner from "../../components/FullPageSpinner";
import {
  hasPurchasedAddOn,
} from "../../utils/event_addons";

const PublicLinks = () => {
  const base = window.location.origin;

  const {
    eventslug,
    eventDoc,
    eventLoading,
    ready,
  } = useEventContext({ redirectIfNotFound: true });

  const [status, setStatus] = useState(null);

  const auctionEnabled = hasPurchasedAddOn(
    eventDoc,
    "auction"
  );

  const routes = useMemo(() => {
    const publicRoutes = [
      {
        label: "Event Home",
        description: "Main public event page.",
        path: "",
      },
      {
        label: "Public Entry Form",
        description: "Public form for registering an entry.",
        path: "public_entry",
      },
      {
        label: "Ballot",
        description: "Voter ballot page.",
        path: "ballot",
      },
      {
        label: "Leaderboard",
        description: "Public voting results page.",
        path: "leaderboard",
      },
      {
        label: "Volunteer Info",
        description: "Volunteer information page.",
        path: "volunteering",
      },
      {
        label: "Volunteer Signup",
        description: "Volunteer opportunity signup page.",
        path: "volunteer_opportunities",
      },
      {
        label: "Sponsors",
        description: "Public sponsor viewer.",
        path: "sponsors_list",
      },
    ];

    if (auctionEnabled) {
      publicRoutes.push(
        {
          label: "Auction",
          description: "Public silent auction landing page.",
          path: "auction",
        },
        {
          label: "Auction Results",
          description: "Public silent auction results page.",
          path: "auction_results",
        }
      );
    }

    return publicRoutes;
  }, [auctionEnabled]);

  const buildUrl = (path) => {
    return `${base}/${eventslug}/${path}`.replace(/\/+$/, "");
  };

  const handleCopy = async (url) => {
    try {
      await navigator.clipboard.writeText(url);

      setStatus({
        type: "success",
        message: "🔗 Link copied to clipboard.",
      });

      setTimeout(() => {
        setStatus(null);
      }, 20000);
    } catch (err) {
      console.error("❌ Failed to copy public link:", err);

      setStatus({
        type: "error",
        message: "Failed to copy link.",
      });

      setTimeout(() => {
        setStatus(null);
      }, 20000);
    }
  };

  if (!ready || eventLoading) {
    return <FullPageSpinner message="Loading public links..." />;
  }

  return (
    <section id="top-content" className="container-fluid py-5">
      <div className="container" style={{ maxWidth: "1400px" }}>
        <div className="row mb-4">
          <div className="col-12 col-lg-8">
            <h1 className="display-5 mb-2">
              🔗 Public Links
            </h1>

            <p className="lead text-muted mb-0">
              Public-facing links and QR codes for flyers, social media, websites,
              email campaigns, and event signage.
            </p>
          </div>

        </div>

        {status?.message && (
          <div
            className={`alert ${
              status.type === "success"
                ? "alert-success"
                : status.type === "warning"
                ? "alert-warning"
                : status.type === "error"
                ? "alert-danger"
                : "alert-info"
            }`}
          >
            {status.message}
          </div>
        )}

        <div className="card shadow-sm mb-4">
          <div className="card-header bg-white">
            <strong>Link Distribution Guide</strong>
          </div>

          <div className="card-body">
            <div className="row g-3">
              <div className="col-12 col-lg-4">
                <div className="border rounded p-3 h-100">
                  <div className="fw-bold mb-2">
                    📱 Social Media
                  </div>

                  <div className="small text-muted">
                    Share event, volunteer, sponsor, leaderboard, and auction links
                    directly to social channels.
                  </div>
                </div>
              </div>

              <div className="col-12 col-lg-4">
                <div className="border rounded p-3 h-100">
                  <div className="fw-bold mb-2">
                    🖨️ Flyers & Signage
                  </div>

                  <div className="small text-muted">
                    Use QR codes on event signage, printed programs, registration
                    desks, and sponsor displays.
                  </div>
                </div>
              </div>

              <div className="col-12 col-lg-4">
                <div className="border rounded p-3 h-100">
                  <div className="fw-bold mb-2">
                    ✉️ Email Campaigns
                  </div>

                  <div className="small text-muted">
                    Include direct public links in volunteer outreach, attendee
                    announcements, and sponsor communications.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {routes.length === 0 ? (
          <div className="card shadow-sm">
            <div className="card-body text-center py-5">
              <h4>No Public Links Available</h4>

              <p className="text-muted mb-0">
                No public-facing routes are currently configured for this event.
              </p>
            </div>
          </div>
        ) : (
          <div className="row g-4">
            {routes.map(({ label, description, path }) => {
              const fullUrl = buildUrl(path);

              return (
                <div
                  key={label}
                  className="col-12 col-md-6 col-xl-4"
                >
                  <div className="card h-100 shadow-sm">
                    <div className="card-header bg-white">
                      <div className="fw-bold">
                        {label}
                      </div>

                      <div className="small text-muted">
                        {description}
                      </div>
                    </div>

                    <div className="card-body">
                      <div className="d-flex justify-content-center mb-4">
                        <div
                          className="border rounded p-3 bg-white"
                          style={{
                            display: "inline-block",
                          }}
                        >
                          <QRCode
                            value={fullUrl}
                            size={180}
                          />
                        </div>
                      </div>

                      <div className="small text-muted mb-2">
                        Public URL
                      </div>

                      <div
                        className="border rounded p-2 bg-light small text-break mb-3"
                        style={{
                          minHeight: "70px",
                        }}
                      >
                        {fullUrl}
                      </div>

                      <div className="d-flex flex-wrap gap-2">
                        <button
                          className="btn btn-outline-primary flex-grow-1"
                          onClick={() => handleCopy(fullUrl)}
                        >
                          📋 Copy Link
                        </button>

                        <a
                          href={fullUrl}
                          className="btn btn-outline-secondary"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          🔗 Open
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );

};

export default PublicLinks;